import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import { ArrowLeftCircle, BookOpen, BadgeCheck, Users, Printer } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StudentReport = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const studentEmail = searchParams.get("email");

  const adminEmail = localStorage.getItem("userEmail") || "";
  const role = localStorage.getItem("userRole");

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [printing, setPrinting] = useState(false);

  const matrix = {
    open_reactive: { label: "1-Kick Start", persona: "Skeptic" },
    open_proactive: { label: "2-False Start", persona: "Addict" },
    cautious_proactive: { label: "3-Self Start", persona: "Explorer" },
    cautious_reactive: { label: "4-Push Start", persona: "Cynic" },
  };

  const readinessStatus = report?.readiness?.status || "cautious";
  const behaviorStyle = report?.behavioral?.style || "proactive";
  const quadrantKey = `${readinessStatus}_${behaviorStyle}`;
  const matchedMatrix = matrix[quadrantKey];

  useEffect(() => {
    if (!studentEmail) {
      navigate("/admin-results");
      return;
    }
    if (!adminEmail) {
      navigate("/register");
      return;
    }
    if (role !== "admin") {
      toast.error("Admin access required");
      navigate("/dashboard");
      return;
    }
    fetchReport();
  }, [studentEmail, adminEmail, role, navigate]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/student-report`, {
        params: { email: studentEmail, admin_email: adminEmail },
        headers: { "x-admin-email": adminEmail },
      });
      setReport(response.data || null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load assessee report");
      setReport(null);
    } finally {
      setLoading(false);
    }
  };

  const handlePrintReport = async () => {
    try {
      setPrinting(true);
      const url = `${API}/admin/student-report-pdf-base64?email=${encodeURIComponent(
        studentEmail || ""
      )}&admin_email=${encodeURIComponent(adminEmail || "")}`;
      const response = await axios.get(url, {
        headers: { "x-admin-email": adminEmail },
      });
      const data = response.data;
      if (!data?.data) {
        throw new Error(data?.detail || "Failed to generate report PDF");
      }
      const binary = atob(data.data || "");
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i += 1) {
        bytes[i] = binary.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: "application/pdf" });
      const objectUrl = window.URL.createObjectURL(blob);
      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "1px";
      iframe.style.height = "1px";
      iframe.style.opacity = "0";
      iframe.style.border = "0";
      iframe.style.pointerEvents = "none";
      iframe.src = objectUrl;
      iframe.onload = () => {
        setTimeout(() => {
          const target = iframe.contentWindow;
          if (!target) return;
          target.onafterprint = () => {
            window.URL.revokeObjectURL(objectUrl);
            iframe.remove();
          };
          target.focus();
          target.print();
        }, 300);
      };
      document.body.appendChild(iframe);
    } catch (error) {
      toast.error(error?.message || "Failed to generate report PDF");
    } finally {
      setPrinting(false);
    }
  };

  const letters = ["A", "B", "C", "D", "E"];
  const fullName = [report?.first_name, report?.last_name].filter(Boolean).join(" ").trim();
  const assessmentCount = [
    report?.readiness ? 1 : 0,
    report?.behavioral ? 1 : 0,
    report?.vision ? 1 : 0,
    report?.values ? 1 : 0,
    report?.learning ? 1 : 0,
    report?.portable_skills ? 1 : 0,
    report?.work_values ? 1 : 0,
  ].reduce((a, b) => a + b, 0);

  return (
    <div>
      <Navbar />
      <div className="dashboard-page">
        <div className="dashboard-container">
          <div className="report-header report-no-print">
            <div className="report-header-left">
              <button
                className="btn-secondary"
                onClick={() => navigate("/admin-results")}
                style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem" }}
              >
                <ArrowLeftCircle size={16} />
                Back to Assessee Reports
              </button>
              <h1>Assessee Report</h1>
            </div>
            <button
              className="btn-secondary"
              onClick={handlePrintReport}
              style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem" }}
              disabled={printing}
            >
              <Printer size={16} />
              {printing ? "Preparing..." : "Print Report"}
            </button>
          </div>

          {loading ? (
            <div className="auth-card"><p>Loading report....</p></div>
          ) : !report ? (
            <div className="empty-state">
              <h3>No results found for this assessee</h3>
            </div>
          ) : (
            <>
              <div className="report-top-grid">
                <div className="report-card report-print-only report-top-card">
                  <div className="report-card-header">
                    <BadgeCheck size={18} />
                    <h3>Assessee Details</h3>
                  </div>
                  <div className="detail-grid">
                    <div className="detail-row">
                      <span>Name</span>
                      <span>{fullName || "N/A"}</span>
                    </div>
                    <div className="detail-row">
                      <span>Email</span>
                      <span>{report.email}</span>
                    </div>
                  </div>
                </div>
                <div className="report-card report-no-print report-top-right">
                  <div className="score-cards responsive-stack report-top-cards">
                    <div className="score-card">
                      <h3>Readiness</h3>
                      <div className="score small-score">
                        {report.readiness?.percentage || 0}%
                      </div>
                      <p className="report-caption">
                        {report.readiness?.status === "open" ? "Open to change" : "Cautious about change"}
                      </p>
                    </div>
                    <div className="score-card">
                      <h3>Behavioral Style</h3>
                      <div className="score small-score" style={{ textTransform: "capitalize" }}>
                        {report.behavioral?.style || "Balanced"}
                      </div>
                      <p className="report-caption">
                        Reactive wins: {report.behavioral?.reactive_wins || 0} - Proactive wins: {report.behavioral?.proactive_wins || 0}
                      </p>
                    </div>
                    <div className="score-card">
                      <h3>Quadrant</h3>
                      <div className="score small-score">
                        {report.quadrant?.label || "N/A"}
                      </div>
                      <p className="report-caption">
                        {report.quadrant?.persona || "No persona"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="report-card report-card-full report-print-only">
                <div className="report-card-header">
                  <Users size={18} />
                  <h3>Strategic Behavioral Action Matrix</h3>
                </div>
                <div className="strategic-table">
                  <div className="strategic-row">
                    <div className={`strategic-cell ${quadrantKey === "open_reactive" ? "active" : ""}`}>
                      <strong>{matrix.open_reactive.label}</strong>
                      <span>({matrix.open_reactive.persona})</span>
                    </div>
                    <div className={`strategic-cell ${quadrantKey === "open_proactive" ? "active" : ""}`}>
                      <strong>{matrix.open_proactive.label}</strong>
                      <span>({matrix.open_proactive.persona})</span>
                    </div>
                  </div>
                  <div className="strategic-row">
                    <div className={`strategic-cell ${quadrantKey === "cautious_reactive" ? "active" : ""}`}>
                      <strong>{matrix.cautious_reactive.label}</strong>
                      <span>({matrix.cautious_reactive.persona})</span>
                    </div>
                    <div className={`strategic-cell ${quadrantKey === "cautious_proactive" ? "active" : ""}`}>
                      <strong>{matrix.cautious_proactive.label}</strong>
                      <span>({matrix.cautious_proactive.persona})</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="report-grid report-no-print">
                <div className="report-card">
                  <div className="report-card-header">
                    <BadgeCheck size={18} />
                    <h3>Readiness Summary</h3>
                  </div>
                  <p>
                    Score {report.readiness?.score || 0} / {report.readiness?.max_score || 0}
                    {" "}({report.readiness?.percentage || 0}%)
                  </p>
                  <p>
                    Status: {report.readiness?.status === "open" ? "Open to change" : "Cautious about change"} (below 40% = cautious)
                  </p>
                  <p>
                    Assessment questions considered: {report.behavioral?.questions_considered || 0}
                  </p>
                </div>
                <div className="report-card">
                  <div className="report-card-header">
                    <Users size={18} />
                    <h3>Reactive vs Proactive</h3>
                  </div>
                  <div className="report-table">
                    <div className="report-row header">
                      <span>Subtotal</span>
                      <span>Reactive</span>
                      <span>Proactive</span>
                    </div>
                    {letters.map((letter) => (
                      <div key={letter} className="report-row">
                        <span>{letter}</span>
                        <span>{report.behavioral?.reactive_subtotals?.[letter] || 0}</span>
                        <span>{report.behavioral?.proactive_subtotals?.[letter] || 0}</span>
                      </div>
                    ))}
                    <div className="report-row total">
                      <span>Total</span>
                      <span>{report.behavioral?.reactive_total || 0}</span>
                      <span>{report.behavioral?.proactive_total || 0}</span>
                    </div>
                  </div>
                </div>
                <div className="report-card">
                  <div className="report-card-header">
                    <BookOpen size={18} />
                    <h3>Assessment 3: Vision</h3>
                  </div>
                  <p>Money: {report.vision?.money ?? "N/A"}</p>
                  <p>Power: {report.vision?.power ?? "N/A"}</p>
                  <p>Knowledge: {report.vision?.knowledge ?? "N/A"}</p>
                </div>
                <div className="report-card">
                  <div className="report-card-header">
                    <BookOpen size={18} />
                    <h3>Assessment 4: Values</h3>
                  </div>
                  <p>Money: {report.values?.money ?? "N/A"}</p>
                  <p>Power: {report.values?.power ?? "N/A"}</p>
                  <p>Knowledge: {report.values?.knowledge ?? "N/A"}</p>
                </div>
                <div className="report-card">
                  <div className="report-card-header">
                    <BookOpen size={18} />
                    <h3>Assessment 5: Learning Style</h3>
                  </div>
                  <p>Choice-1 (Visual): {report.learning?.counts?.choice1 ?? 0}</p>
                  <p>Choice-2 (Auditory): {report.learning?.counts?.choice2 ?? 0}</p>
                  <p>Choice-3 (Experiential): {report.learning?.counts?.choice3 ?? 0}</p>
                  <p>Dominant: {(report.learning?.dominant || []).join(", ") || "N/A"}</p>
                </div>
                <div className="report-card">
                  <div className="report-card-header">
                    <BookOpen size={18} />
                    <h3>Assessment 6: Portable Skills</h3>
                  </div>
                  <p>Average Confidence: {report.portable_skills?.average ?? "N/A"}</p>
                  <p>Total Items: {report.portable_skills?.total_items ?? 0}</p>
                  <p>Feel Joyful (Yes): {report.portable_skills?.joyful_yes ?? 0}</p>
                  <p>Feel Develop (Yes): {report.portable_skills?.develop_yes ?? 0}</p>
                  {(report.portable_skills?.categories || []).map((c) => (
                    <p key={c.name}>
                      {c.name.replace("Assessment-6: Portable Skills - ", "")}: {c.average ?? "N/A"}
                    </p>
                  ))}
                </div>
                <div className="report-card">
                  <div className="report-card-header">
                    <BookOpen size={18} />
                    <h3>Assessment 7: Work Values</h3>
                  </div>
                  <p>Total Score: {report.work_values?.total_score ?? 0} / {report.work_values?.max_score ?? 0}</p>
                  <p>Percentage: {report.work_values?.percentage ?? 0}%</p>
                  <p>
                    Top {(report.work_values?.top_n || []).length} values that you look for in your work are:
                  </p>
                  {(report.work_values?.top_n || []).map((item, idx) => (
                    <p key={`${item.key || item.label}-${idx}`}>
                      {idx + 1}. {item.label}
                    </p>
                  ))}
                </div>
              </div>

              <div className="report-card report-card-full report-print-only">
                <div className="report-card-header">
                  <BookOpen size={18} />
                  <h3>{`Assessment Report (1-${assessmentCount || 0})`}</h3>
                </div>
                <div className="report-table">
                  <div className="report-row header">
                    <span>Assessment</span>
                    <span>Summary</span>
                    <span>Result</span>
                  </div>
                  <div className="report-row">
                    <span>A1 Readiness</span>
                    <span>
                      {report.readiness?.score || 0}/{report.readiness?.max_score || 0}
                      {" "}({report.readiness?.percentage || 0}%)
                    </span>
                    <span>{report.readiness?.status === "open" ? "Open" : "Cautious"}</span>
                  </div>
                  <div className="report-row">
                    <span>A2 Behavioral</span>
                    <span>Reactive {report.behavioral?.reactive_total || 0} / Proactive {report.behavioral?.proactive_total || 0}</span>
                    <span>{report.behavioral?.style || "Balanced"}</span>
                  </div>
                  <div className="report-row">
                    <span>A3 Vision</span>
                    <span>Money {report.vision?.money ?? "N/A"} | Power {report.vision?.power ?? "N/A"} | Knowledge {report.vision?.knowledge ?? "N/A"}</span>
                    <span></span>
                  </div>
                  <div className="report-row">
                    <span>A4 Values</span>
                    <span>Money {report.values?.money ?? "N/A"} | Power {report.values?.power ?? "N/A"} | Knowledge {report.values?.knowledge ?? "N/A"}</span>
                    <span></span>
                  </div>
                  <div className="report-row">
                    <span>A5 Learning</span>
                    <span>Visual {report.learning?.counts?.choice1 ?? 0} | Auditory {report.learning?.counts?.choice2 ?? 0} | Experiential {report.learning?.counts?.choice3 ?? 0}</span>
                    <span>{(report.learning?.dominant || []).join(", ") || "N/A"}</span>
                  </div>
                  <div className="report-row">
                    <span>A6 Portable Skills</span>
                    <span>
                      Average {report.portable_skills?.average ?? "N/A"} | Items {report.portable_skills?.total_items ?? 0} | Joyful Yes {report.portable_skills?.joyful_yes ?? 0} | Develop Yes {report.portable_skills?.develop_yes ?? 0}
                    </span>
                    <span></span>
                  </div>
                  <div className="report-row">
                    <span>A7 Work Values</span>
                    <span>
                      Score {report.work_values?.total_score ?? 0}/{report.work_values?.max_score ?? 0} ({report.work_values?.percentage ?? 0}%)
                    </span>
                    <span>{(report.work_values?.top5 || []).map((item) => item.label).slice(0, 2).join(", ") || "N/A"}</span>
                  </div>
                </div>
              </div>

              {report.quadrant?.strategies?.length > 0 && (
                <div className="report-card report-card-full report-print-only">
                  <div className="report-card-header">
                    <BookOpen size={18} />
                    <h3>Matched Description: {report.quadrant.persona}</h3>
                  </div>
                  <div className="strategy-list">
                    {report.quadrant.strategies.map((item, idx) => (
                      <div key={idx} className="strategy-item">
                        <h4>{item.title}</h4>
                        <p>{item.body}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default StudentReport;
